/// <amd-module name="Wit/SprintsHub/SprintsView/Services/ITaskboardService" />
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IIterationData } from "Agile/Contracts/Common";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IFilter } from "VSSUI/Utilities/Filter";
import { IObservableClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { IClientWorkItemService } from "Wit/ClientWorkItemService/IClientWorkItemService";
import { IChangeIterationWorkItemData, ICurrentIterationData } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FilterManager, IBoardFilterDataSource } from "Wit/Filter/FilterManager";
import { TaskboardGroupBy } from "Wit/SprintsHub/Common/Constants";
import { ISprintTaskboardData, ISprintTaskboardErrorData, ITaskboardWorkItem } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemEventData } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { ITaskboardRow } from "../Components/Taskboard/ITaskboardRow";

export interface ITaskboardService extends IClientWorkItemService<IObservableClientWorkItem<ITaskboardWorkItem>> {
    actualizeChildItem(child: IObservableClientWorkItem<ITaskboardWorkItem>, originalId: number): void;
    createNewWorkItem(
        workItemType: string,
        title: string,
        workItem: IObservableClientWorkItem<ITaskboardWorkItem>,
        tempItemId: number
    ): Promise<void>;
    getAllRows: () => IObservableArray<ITaskboardRow>;
    getBacklogConfiguration: () => IBacklogConfiguration | undefined;
    getFilter(): IFilter;
    getFilterManager(): FilterManager;
    getFilterSource(): IBoardFilterDataSource;
    getFilteredRows: () => IObservableArray<ITaskboardRow>;
    getInitializedTaskboardData(): ISprintTaskboardData;
    getRemainingWorkMap(): Map<string, ObservableValue<number>>;
    resetColumnIdToRemainingWorkMapValues(): void;
    getSomeRowsExpanded(): ObservableValue<boolean>;
    hasRowData(): boolean;
    getTaskBoardData: () => IObservableValue<ISprintTaskboardData | undefined>;
    getTaskBoardErrorData: () => IObservableValue<ISprintTaskboardErrorData | undefined>;
    getTaskboardRowMap(): Map<string, ITaskboardRow>;
    getWorkItemColumnMap(): IObservableValue<{
        [workItemId: number]: string;
    }>;
    getWorkItemTypeColorAndIcons: () => WorkItemTypeColorAndIcon[] | undefined;
    groupBy: ObservableValue<TaskboardGroupBy>;
    hasAgileToolsBacklogManagementPermission: () => boolean;
    initializeNewItem(fieldMap: INamedFieldDataDictionary, parentId?: number): IObservableClientWorkItem<ITaskboardWorkItem>;
    initializeRows(ignoreCache?: boolean): void;
    /**
     * Checks whether the state field is complete
     *
     * @param workItem The work item we are evaluating
     * @return True if the state field is mapped to complete metastate, otherwise false
     */
    isStateComplete(workItem: ITaskboardWorkItem): boolean;
    reloadTaskBoardData: () => Promise<void>;
    removeWorkItem: (workItemId: number, options?: { processChildItems?: boolean }) => Promise<void>;
    selectedIteration: IIterationData;
    setIteration: (iteration: IIterationData) => void;
    setEvaluatePeopleFilter: (evaluatePeopleFilter: (rows?: ITaskboardRow[]) => boolean) => void;
    setSomeRowsExpanded: (expanded: boolean) => void;
    updateItem: (itemId: number, fieldReferenceName: string, newValue: any) => Promise<string | undefined>;
    watchTeamTaskboard(teamId: string): void;
    workItemChangedCallback({ workItem, changedWorkItemLinks }: IWorkItemEventData): void;
    updateIteration(
        currentIterationData: ICurrentIterationData,
        item?: ITaskboardWorkItem,
        successCallback?: (workItemDTO: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number) => Promise<void>,
        useChangeIterationPerfomanceOptimization?: boolean
    ): Promise<void>;
    needToSkipThisChildTaskForRemainingHoursCalculation(childWorkItem: IObservableClientWorkItem<ITaskboardWorkItem>): boolean;
    hasPermissionToDeleteWorkItems(): ObservableValue<boolean>;
    updateBackgroundColorForFilteredTaskboardRows(): void;
}
